import { useState } from 'react';

import {
  Box,
  FormikField,
  HStack,
  IconButton,
  Input,
  ListRow,
  Separator,
  Shimmer,
  ShimmerText,
  Slider,
  Text,
  VisuallyHidden,
} from '@betterment/samba-react';
import { iconDisplayForSecurity } from '@betterment/shared-components/src/utils/accountSecurityHelpers';
import { FieldArray, useFormikContext } from 'formik';
import { Fragment } from 'react/jsx-runtime';

import { SecurityGroupAllocation } from './types';

const SecurityGroupAllocationRowShimmer = () => (
  <Box aria-busy="true" aria-label="Loading..." role="status" css={{ paddingY: 'space.300' }}>
    <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.200' }}>
      <Shimmer css={{ height: 'size.500', width: 'size.500', flexShrink: 0 }} />
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.100', flex: 1 }}>
        <ShimmerText textStyle="text50" css={{ width: '25%' }} />
        <ShimmerText textStyle="text50" css={{ width: '55%' }} />
      </Box>
      <Shimmer css={{ height: 'size.450', width: '6em' }} />
    </Box>
    <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.200', paddingTop: 'space.150' }}>
      <Shimmer css={{ height: 'size.400', width: 'size.400', flexShrink: 0 }} />
      <Shimmer css={{ height: 'size.100', flex: 1 }} />
    </Box>
  </Box>
);

type SecurityGroupAllocationRowProps = {
  securityGroupAllocation: SecurityGroupAllocation;
  index: number;
  onRemove: () => void;
};

const SecurityGroupAllocationRow = ({ securityGroupAllocation, index, onRemove }: SecurityGroupAllocationRowProps) => {
  const { setFieldValue } = useFormikContext();
  const {
    securityGroup: { primarySecurity },
  } = securityGroupAllocation;
  const [isPendingRemoval, setIsPendingRemoval] = useState(false);
  const targetWeightFieldName = `securityGroupAllocations[${index}].targetWeight`;

  const handleRemove = () => {
    setIsPendingRemoval(true);
    setTimeout(onRemove, 400);
  };

  if (isPendingRemoval) {
    return <SecurityGroupAllocationRowShimmer />;
  }

  return (
    <ListRow css={{ paddingX: 0 }}>
      <ListRow.LeadingAsset>
        {iconDisplayForSecurity({ symbol: primarySecurity.symbol, logo: primarySecurity.logo })}
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text color="text.selected" variant="text50">
          {primarySecurity.symbol}
        </Text>
        <Text variant="text50">{primarySecurity.name}</Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingContent css={{ marginLeft: 'space.150' }}>
        <FormikField name={targetWeightFieldName}>
          <FormikField.Label>
            <VisuallyHidden>Target weight for {primarySecurity.name}</VisuallyHidden>
          </FormikField.Label>
          <FormikField.Control>
            <Input alignment="right" trailingSymbol="%" type="number" spacing="compact" css={{ width: '6em' }} />
          </FormikField.Control>
        </FormikField>
      </ListRow.TrailingContent>
      <ListRow.BottomContent>
        <HStack css={{ gap: 'space.200' }}>
          <IconButton
            css={{ bg: 'background.neutral' }}
            spacing="compact"
            label={`Remove ${primarySecurity.name}`}
            name="X"
            onClick={handleRemove}
          />
          <Slider
            min={0}
            max={100}
            value={securityGroupAllocation.targetWeight}
            onValueChange={val => setFieldValue(targetWeightFieldName, val)}
          />
        </HStack>
      </ListRow.BottomContent>
    </ListRow>
  );
};

export const SecurityGroupAllocationFields = () => {
  const {
    values: { securityGroupAllocations },
  } = useFormikContext<{ securityGroupAllocations: SecurityGroupAllocation[] }>();

  return (
    <FieldArray name="securityGroupAllocations">
      {arrayHelpers =>
        securityGroupAllocations.map((securityGroupAllocation, index) => {
          const {
            securityGroup: { primarySecurity },
          } = securityGroupAllocation;

          return (
            <Fragment key={primarySecurity.id}>
              <Separator />
              <SecurityGroupAllocationRow
                securityGroupAllocation={securityGroupAllocation}
                index={index}
                onRemove={() => arrayHelpers.remove(index)}
              />
            </Fragment>
          );
        })
      }
    </FieldArray>
  );
};
